import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'
import ModalWrapper from '../../../components/UI/ModalWrapper'
import { ReactComponent as CloseIcon } from '../../../assets/icons/cross.svg'
import Map from '../../../components/Map'
import Input from '../../../components/UI/Input'
import { useGetFillTruckQuery, useRefuellTruckMutation } from '../../../store/trucks/trucks.api'
import Loader from '../../../components/UI/Loader'
import { IMarker, IPoint, IRefuellingData, ISelectOption } from '../../../models/models'
import classNames from 'classnames'
import s from './index.module.scss'
import Button from '../../../components/UI/Button'
import { useActions } from '../../../hooks/useActions'
import Select from '../../../components/UI/Select'
import { FillType } from '../../../store/trucks/trucks.slice'
import RegionSelect from '../../../components/UI/RegionSelect'

interface IProps {
	id: number
	type: FillType
}

const TrucksFillModal: React.FC<IProps> = ({ id, type }) => {
	const [selectedRegion, setSelectedRegion] = useState('')

	const { data, isLoading } = useGetFillTruckQuery({ type, id, regionId: selectedRegion }, { skip: !selectedRegion })
	const [refuellTruck, { error: refuellError }] = useRefuellTruckMutation()

	const { setFillObj } = useActions()

	const [isAnimate, setIsAnimate] = useState<boolean>(false)
	const [volume, setVolume] = useState<number>(0)
	const [markers, setMarkers] = useState<IMarker[]>([])
	const [showChangeLocation, setShowChangeLocation] = useState<boolean>(false)
	const [canChangeLocation, setCanChangeLocation] = useState<boolean>(false)
	const [productId, setProductId] = useState<number | string>('')
	const [flagCoords, setFlagCoords] = useState<IPoint | null>(null)
	const [selectTypeIsVisible, setSelectTypeIsVisible] = useState<boolean>(false)
	const [selectTypeOptions, setSelectTypeOptions] = useState<ISelectOption[]>([])
	const [selectedRefueller, setSelectedRefueller] = useState<{
		id: number
		type: 'truck' | 'gas' | 'trailer'
		name: string
	} | null>(null)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const [error, setError] = useState<string>('')

	const onClose = useCallback(() => {
		setIsAnimate(true)
		setTimeout(() => {
			setFillObj(null)
		}, 300)
	}, [])

	const calcSelectTypeOptions = (obj: typeof selectedRefueller): ISelectOption[] => {
		if (!obj) return []

		const truck = data?.refuellers?.find((i) => Number(i?.trailer?.id) === obj.id)

		return [
			{ value: JSON.stringify(obj), title: 'Причеп' },
			{
				value: JSON.stringify({
					id: truck?.id,
					type: 'truck',
					name: truck?.number,
				}),
				title: 'Паливовоз',
			},
		]
	}

	const changeHandler = (ev: ChangeEvent<HTMLInputElement>) => {
		if (Number(ev.target.value)) {
			setVolume(Number(ev.target.value))
		} else if (ev.target.value === '') {
			setVolume(0)
		}
	}

	const selectProductHandler = (ev: ChangeEvent<HTMLSelectElement>) => {
		setSelectTypeIsVisible(false)
		setSelectedRefueller(null)
		setError('')
		setProductId(ev.target.value)
	}

	const submitHandler = () => {
		if (!volume) {
			setError('Введіть кількість літрів')
			return
		}
		if (!selectedRefueller?.id) {
			setError('Оберіть заправника або колонку')
			return
		}

		setError('')
		setIsSubmitting(true)

		let refuellingData = {
			filledTankId: type === 'truck' ? data?.currentTruck.id : data?.currentTruck.trailer?.id,
			filledTankType: type,
			ordered: volume,
			productId,
		} as IRefuellingData

		const initialCoords = data?.currentTruck.point

		if (canChangeLocation && flagCoords && (flagCoords.latitude !== initialCoords?.latitude || flagCoords.longitude !== initialCoords?.longitude)) {
			refuellingData = {
				...refuellingData,
				type: 2,
				refuellerTankId: selectedRefueller.id,
				refuellerTankType: selectedRefueller.type,
				filling_point: { lat: flagCoords.latitude, lng: flagCoords.longitude },
			}
		} else if (selectedRefueller.type === 'gas') {
			refuellingData = {
				...refuellingData,
				type: 1,
				gasStationId: selectedRefueller.id,
			}
		} else if (selectedRefueller.type === 'truck' || selectedRefueller.type === 'trailer') {
			refuellingData = {
				...refuellingData,
				type: data?.currentTruck?.point ? 4 : 3,
				refuellerTankId: selectedRefueller.id,
				refuellerTankType: selectedRefueller.type,
			}
		}

		refuellTruck(refuellingData)
			.unwrap()
			.then((res) => {
				if (res.status === 'success') {
					onClose()
				}
			})
			.catch(() => setIsSubmitting(false))
	}

	useEffect(() => {
		const listener = (event: KeyboardEvent) => {
			if (event.code === 'Escape') {
				onClose()
			}
		}

		document.addEventListener('keydown', listener)
		return () => document.removeEventListener('keydown', listener)
	}, [])

	useEffect(() => {
		if (data) {
			if (data?.currentTruck?.point && !flagCoords) {
				setFlagCoords(data.currentTruck.point)
				return
			}
			if (!productId) {
				setProductId(type === 'truck' ? data.currentTruck.products[0].id : data.currentTruck?.trailer?.products[0]?.id || 0)
				return
			}

			const flagMarker: IMarker = {
				...data.currentTruck,
				type: 'flag',
				draggable: canChangeLocation,
				point: flagCoords as IPoint,
				callback: setFlagCoords,
			}

			const refuellers: IMarker[] = data.refuellers
				.filter(
					(i) =>
						(i.products.find((j) => Number(j.id) === Number(productId))?.amount || 0) > 0 || (i?.trailer?.products?.find((j) => Number(j.id) === Number(productId))?.amount || 0) > 0,
				)
				.map((i) => {
					const type = i?.trailer ? 'trailer' : 'truck'
					const id = type === 'truck' ? i.id : i.trailer?.id

					return {
						...i,
						id: `${type}_${id}`,
						type: type,
						amount: i.products.find((j) => Number(j.id) === Number(productId))?.amount || 0,
						...(type === 'trailer' && {
							trailerAmount: i?.trailer?.products?.find((j) => Number(j.id) === Number(productId))?.amount || 0,
						}),
						callback: () => {
							const obj: typeof selectedRefueller = {
								id: Number(id),
								type: type,
								name: (type === 'truck' ? i.number : i.trailer?.number) || '',
							}
							setSelectTypeIsVisible(type === 'trailer')
							setSelectedRefueller(obj)
							if (type === 'trailer') {
								setSelectTypeOptions(calcSelectTypeOptions(obj))
							}
							setShowChangeLocation(true)
							if (flagCoords) {
								setMarkers((prev) => [...prev, flagMarker])
							}
						},
					}
				})

			const gasStations: IMarker[] = data.gasStations
				.filter((i) => {
					const productType =
						type === 'truck'
							? data.currentTruck.products.find((j) => Number(j.id) === Number(productId))?.type
							: data.currentTruck?.trailer?.products.find((j) => Number(j.id) === Number(productId))?.type

					return productType === 'adBlue' ? i.canFillAdblue : true
				})
				.map((i) => ({
					...i,
					id: `gas_${i.id}`,
					point: i.location,
					type: 'gas',
					callback: () => {
						setSelectedRefueller({
							id: Number(i.id),
							type: 'gas',
							name: i.name || '',
						})
						setShowChangeLocation(true)
						if (flagCoords) {
							setMarkers((prev) => [...prev, flagMarker])
						}
					},
				}))

			setMarkers([
				...refuellers,
				...gasStations,
				...(flagCoords
					? ([
							{
								...data.currentTruck,
								type: 'point',
								draggable: false,
							},
							...(canChangeLocation ? [flagMarker] : []),
						] as IMarker[])
					: []),
			])
		}
	}, [data, canChangeLocation, flagCoords, productId])

	return (
		<ModalWrapper closeHandler={onClose} isAnimate={isAnimate}>
			<div
				className={classNames(s.wrapper, {
					[s.animate]: isAnimate,
				})}
			>
				<CloseIcon className={s.closeIcon} onClick={onClose} />

				<div className={s.formWrapper}>
					<div className={s.form}>
						<h3 className={s.title}>Виберіть місце заправки</h3>
						<RegionSelect
							value={selectedRegion}
							onChange={setSelectedRegion}
						/>
						{(isLoading || !selectedRegion) ? (
							<Loader />
						) : (
							<>
								<p className={s.text}>Запрaвка {type === 'truck' ? 'паливовоза' : 'причепа'}</p>
								<p className={s.text}>{type === 'truck' ? data?.currentTruck?.number : data?.currentTruck?.trailer?.number}</p>
								<p className={s.text}>Необхідна кількість</p>
								<p className={s.text}>{type === 'truck' ? data?.currentTruck?.needFuel : data?.currentTruck?.trailer?.needFuel} л</p>

								{selectTypeIsVisible && (
									<>
										<p className={s.text}>Виберіть заправника</p>
										<Select
											options={selectTypeOptions}
											value={JSON.stringify(selectedRefueller)}
											onChange={(ev: ChangeEvent<HTMLSelectElement>) => setSelectedRefueller(JSON.parse(ev.target.value))}
											className={s.select}
										/>
									</>
								)}

								<p className={s.text}>Виберіть товар</p>
								<Select
									options={
										type === 'truck'
											? data?.currentTruck?.products?.map((i) => ({
													value: i.id,
													title: i.name,
												})) || []
											: data?.currentTruck?.trailer?.products?.map((i) => ({
													value: i.id,
													title: i.name,
												})) || []
									}
									value={productId}
									onChange={selectProductHandler}
									className={s.select}
								/>

								<p className={s.text}>Кількість, л</p>
								<Input name={'volume'} value={volume} onChange={changeHandler} className={s.input} />

								{flagCoords && (
									<p className={classNames(s.text, s.selectedText)}>
										Місце заправки: {flagCoords?.latitude}, {flagCoords?.longitude}
									</p>
								)}

								{selectedRefueller && (
									<p className={classNames(s.text, s.selectedText)}>
										Обрана точка заправки: <br />
										{selectedRefueller.type === 'gas' && 'Нафтосховище'}
										{selectedRefueller.type === 'truck' && 'Паливовоз'}
										{selectedRefueller.type === 'trailer' && 'Причеп'}
										<br />
										{selectedRefueller.name}
									</p>
								)}

								{flagCoords && showChangeLocation && (
									<div
										className={classNames(s.changeLocation, {
											[s.margin]: selectedRefueller,
										})}
										onClick={() => setCanChangeLocation(true)}
									>
										Змінити точку заправки
									</div>
								)}

								<Button variant="filled" className={s.btn} disabled={isSubmitting} onClick={submitHandler}>
									{isSubmitting ? 'Зачекайте' : 'Підтвердити'}
								</Button>

								{error && <p className={s.error}>{error}</p>}

								{refuellError && 'data' in refuellError && <p className={s.error}>{refuellError?.data?.message}</p>}
							</>
						)}
					</div>
					<div className={s.mapWrapper}>
						<Map markers={markers} />
					</div>
				</div>
			</div>
		</ModalWrapper>
	)
}

export default TrucksFillModal
